import { publicAssetUrl } from './publicAssetUrl.js';

/** @type {Map<string, Promise<string>>} */
const inflight = new Map();

/**
 * Icon file slug. Accepts snake_case (`arrow_forward`) and kebab-case
 * (`arrow-forward`). Hyphens stay, because the files in public/img/svg use them.
 *
 * @param {string} name
 */
export function slugFromIconName(name) {
    const raw = String(name || 'help').trim().toLowerCase();
    const slug = raw
        .replace(/[^a-z0-9_-]/g, '')
        .replace(/_/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

    return slug || 'help';
}

/**
 * Match App\Support\MaterialSymbolSvg::prepareSvgContents() so injected
 * markup keeps currentColor, the material-symbol-svg class, and the
 * size rules in app.css.
 *
 * @param {string} raw
 */
export function prepareSvgContents(raw) {
    let svg = String(raw || '').replace(/^\uFEFF/, '').trim();
    svg = svg.replace(/<\?xml\s[^>]*\?>\s*/i, '');
    svg = svg.replace(
        'xmlns="https://www.w3.org/2000/svg"',
        'xmlns="http://www.w3.org/2000/svg"'
    ).trim();

    if (!/^<svg\s[^>]*>[\s\S]*<\/svg>\s*$/i.test(svg)) {
        return '';
    }

    const match = svg.match(/^<svg([^>]*)>([\s\S]*)<\/svg>\s*$/i);
    if (!match) {
        return '';
    }

    let attrs = match[1] || '';
    const inner = match[2] || '';

    attrs = attrs.replace(/\swidth\s*=\s*"[^"]*"/gi, '');
    attrs = attrs.replace(/\sheight\s*=\s*"[^"]*"/gi, '');
    attrs = attrs.replace(/\sstyle\s*=\s*"[^"]*"/gi, '');

    const classMatch = attrs.match(/\bclass\s*=\s*"([^"]*)"/i);
    if (classMatch) {
        const merged = `material-symbol-svg ${classMatch[1]}`.trim().replace(/\s+/g, ' ');
        attrs = attrs.replace(
            /\bclass\s*=\s*"[^"]*"/i,
            `class="${merged.replace(/"/g, '&quot;')}"`
        );
    } else {
        attrs += ' class="material-symbol-svg"';
    }

    if (!/\saria-hidden\s*=/i.test(attrs)) {
        attrs += ' aria-hidden="true"';
    }
    if (!/\sfocusable\s*=/i.test(attrs)) {
        attrs += ' focusable="false"';
    }

    return `<svg ${attrs.trim()}>${inner}</svg>`;
}

/**
 * @param {string} slug
 */
async function fetchPreparedSvg(slug) {
    const response = await fetch(publicAssetUrl(`/img/svg/${slug}.svg`), {
        credentials: 'same-origin',
    });
    if (!response.ok) {
        return '';
    }

    const text = await response.text();
    const trimmed = text.trim();
    if (!trimmed.startsWith('<svg') && !trimmed.startsWith('<?xml')) {
        return '';
    }

    return prepareSvgContents(text);
}

/**
 * One inline SVG for a kebab-case slug. Missing files fall back to help.svg.
 * Does not import the material-symbol-svgs atlas.
 *
 * @param {string} slug
 * @returns {Promise<string>}
 */
export function getMaterialSymbolSvg(slug) {
    const safe = slugFromIconName(slug);
    const existing = inflight.get(safe);
    if (existing) {
        return existing;
    }

    const promise = fetchPreparedSvg(safe)
        .then((prepared) => {
            if (prepared) {
                return prepared;
            }
            if (safe === 'help') {
                return '';
            }

            return getMaterialSymbolSvg('help');
        })
        .catch(() => {
            if (safe === 'help') {
                return '';
            }

            return getMaterialSymbolSvg('help');
        });

    inflight.set(safe, promise);

    return promise;
}
